import './host'

import { RegionalUserService } from '@graft/nuget-gc.webportals.regionalusers'

import { getSessionToken } from '@common/core/helpers'
import { isVisionLocal } from '@common/core/utils/isVisionLocal'
import { withTiming } from '@common/core/utils/withTiming'

import { buildRegionalUserData } from './mappers/buildRegionalUserData'
import { regionalUserToObject } from './objects/regional-user.object'
import { RegionalUser } from './types/user.type'

export async function addUserRegionalData(regionalUser: RegionalUser): Promise<string | null> {
  try {
    if (isVisionLocal() || !regionalUser) return null

    const regionalUserData = buildRegionalUserData(regionalUser)

    const token = await getSessionToken()
    return RegionalUserService.addUserRegionalData(token, regionalUserData)
  } catch (error) {
    console.error('Failed to add user regional data:', error)
    throw error
  }
}

export async function getRegionalUsersByUserIds(userIds: string[]): Promise<RegionalUser[]> {
  if (isVisionLocal() || userIds.length === 0) return []

  const normalizedUserIds = Array.from(new Set(userIds.filter(Boolean))).sort()
  if (normalizedUserIds.length === 0) return []

  try {
    return await withTiming('getRegionalUsersByUserIds', async () => {
      const token = await getSessionToken()
      const graftcodeData = await RegionalUserService.getRegionalUsersByUserIds(token, normalizedUserIds)
      if (!graftcodeData?.length) return []

      return Promise.all(graftcodeData.map(regionalUserToObject))
    })()
  } catch (error) {
    console.error('Failed to get regional users by user ids:', error)
    return []
  }
}

export async function getUserRegionalData(): Promise<RegionalUser | null> {
  try {
    if (isVisionLocal()) return null

    const token = await getSessionToken()
    const graftcodeData = await withTiming('getUserRegionalData', async () =>
      RegionalUserService.getUserRegionalData(token)
    )()

    return graftcodeData ? await regionalUserToObject(graftcodeData) : null
  } catch (error) {
    console.error('Failed to get user regional data:', error)
    throw error
  }
}

export async function updateUserNames(firstName: string, lastName: string): Promise<void> {
  if (isVisionLocal()) return

  try {
    const token = await getSessionToken()
    await RegionalUserService.updateUserNames(token, firstName, lastName)
  } catch (error) {
    console.error('Failed to update regional user names:', error)
    throw error
  }
}

export async function updateUserEmail(userEmail: string): Promise<void> {
  if (isVisionLocal()) return

  try {
    const token = await getSessionToken()
    await RegionalUserService.updateUserEmail(token, userEmail)
  } catch (error) {
    console.error('Failed to update regional user email:', error)
    throw error
  }
}

export async function updateUserAvatar(avatar: number[]): Promise<void> {
  if (isVisionLocal()) return

  try {
    const token = await getSessionToken()
    await RegionalUserService.updateUserAvatar(token, avatar)
  } catch (error) {
    console.error('Failed to update regional user avatar:', error)
    throw error
  }
}
